import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Modal } from '@components/common';
import { CALENDLY_URL } from '@constants';

export default function CTA() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Get form data
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Submit to Netlify
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then(() => {
        setIsSubmitted(true);
        // Open brochure in new tab
        window.open('/brochure.pdf', '_blank');
        // Close modal after a brief delay
        setTimeout(() => {
          setIsModalOpen(false);
          setIsSubmitted(false);
        }, 1500);
      })
      .catch(error => {
        console.error('Form submission error:', error);
      });
  };

  return (
    <>
      <section id="contact" className="py-24 gradient-navy cta-photo relative overflow-hidden">
        {/* Decorative glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-teal/5 blur-3xl pointer-events-none" />

        <div className="container-custom relative text-center">
          <div className="animate-fade-up">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-primary-foreground mb-6">
              Ready to Secure Your Business?
            </h2>
            <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
              Book a discovery call to discuss your pressure points; AI adoption, regulatory
              scrutiny, customer due diligence, operational resilience - and find out how a{' '}
              <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
                fractional CISO
              </Link>{' '}
              can transform your business.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero text-base px-10"
              >
                Book a Call <ArrowRight className="ml-1 h-4 w-4" />
              </a>
              <button
                onClick={() => setIsModalOpen(true)}
                className="btn-hero-outline text-base px-10"
              >
                Download Our Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Brochure Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Download Our Brochure"
      >
        {isSubmitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-teal/20 flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-teal"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h4 className="text-lg font-heading font-semibold text-foreground mb-2">Thank you!</h4>
            <p className="text-muted-foreground">Your brochure is opening in a new tab.</p>
          </div>
        ) : (
          <>
            <p className="text-muted-foreground mb-6">
              Join our mailing list to receive the latest insights on cybersecurity leadership and
              get instant access to our brochure.
            </p>

            <form
              name="brochure-download"
              method="POST"
              data-netlify="true"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* Hidden field for Netlify */}
              <input type="hidden" name="form-name" value="brochure-download" />

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  placeholder="you@company.com"
                  className="w-full px-4 py-3 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors"
                />
              </div>

              <button type="submit" className="w-full btn-hero justify-center">
                Get the Brochure <ArrowRight className="ml-2 h-4 w-4" />
              </button>

              <p className="text-xs text-muted-foreground text-center">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </form>
          </>
        )}
      </Modal>
    </>
  );
}
